import { ListAmount } from '@/components/InvoiceReportDetailDrawer/parts/ListAmount';
import { SectionHeading } from '@/components/InvoiceReportDetailDrawer/parts/SectionHeading';
import {
  TaxAnalyze,
  TaxRateItem,
} from '@/components/InvoiceReportDetailDrawer/parts/TaxAnalyze';
import { ListViewBody, ListViewLabel } from '@/components/ListView';
import { useTranslation } from '@/i18n';
import { Bottom, ListView, Top } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

export type BasicInfoData = {
  office_member?: { display_name?: string };
  report_dept?: { name?: string };
  agent_applicant_office_member_name?: string;
  status?: string;
  created_at?: string;
  submitted_at?: string;
  approved_at?: string;
  total_value?: string;
  total_excise_value?: string;
  total_withholding_income_tax_value?: string;
  foreign_currency_amount?: string;
  total_transaction_details_value?: string;
  tax_rate_items?: TaxRateItem[];
};

export type BasicInfoSectionProps = {
  data?: BasicInfoData;
  initialOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

export const BasicInfoSection: FC<BasicInfoSectionProps> = memo(
  ({ data, initialOpen, onToggle }) => {
    const { t } = useTranslation();

    return (
      <SectionHeading
        initialOpen={initialOpen}
        title={t('approving_invoice_report_detail_basic_info')}
        onToggle={onToggle}
      >
        <div className={cx('listViewContainer')}>
          <ListView>
            <Top>
              <ListViewLabel key='left'>
                {t('approving_invoice_report_detail_applicant')}
              </ListViewLabel>
              <ListViewBody key='right' className={cx('listViewBody')}>
                {data?.office_member?.display_name}
              </ListViewBody>
            </Top>
            {data?.report_dept && (
              <Top>
                <ListViewLabel key='left'>
                  {t('approving_invoice_report_detail_department')}
                </ListViewLabel>
                <ListViewBody className={cx('listViewBody')} key='right'>
                  {data?.report_dept?.name}
                </ListViewBody>
              </Top>
            )}
            {data?.agent_applicant_office_member_name && (
              <Top>
                <ListViewLabel key='left'>
                  {t('approving_invoice_report_detail_agent_applicant')}
                </ListViewLabel>
                <ListViewBody key='right' className={cx('listViewBody')}>
                  {data?.agent_applicant_office_member_name}
                </ListViewBody>
              </Top>
            )}
            <Top>
              <ListViewLabel key='left'>
                {t('approving_invoice_report_detail_application_status')}
              </ListViewLabel>
              <ListViewBody key='right' className={cx('listViewBody')}>
                {data?.status}
              </ListViewBody>
            </Top>
            <Top>
              <ListViewLabel key='left'>
                {t('approving_invoice_report_detail_created_at')}
              </ListViewLabel>
              <ListViewBody key='right' className={cx('listViewBody')}>
                {data?.created_at}
              </ListViewBody>
            </Top>
            <Top>
              <ListViewLabel key='left'>
                {t('approving_invoice_report_detail_application_date')}
              </ListViewLabel>
              <ListViewBody key='right' className={cx('listViewBody')}>
                {data?.submitted_at}
              </ListViewBody>
            </Top>
            <Top>
              <ListViewLabel key='left'>
                {t('approving_invoice_report_detail_approval_date')}
              </ListViewLabel>
              <ListViewBody key='right' className={cx('listViewBody')}>
                {data?.approved_at}
              </ListViewBody>
            </Top>
            <Top>
              <ListAmount
                totalValue={data?.total_value}
                consumptionTax={data?.total_excise_value}
                withholdingTax={data?.total_withholding_income_tax_value}
                foreignCurrency={data?.foreign_currency_amount}
                totalTransactionDetailsValue={
                  data?.total_transaction_details_value
                }
              />
            </Top>
            <Bottom>
              <TaxAnalyze data={data?.tax_rate_items} />
            </Bottom>
          </ListView>
        </div>
      </SectionHeading>
    );
  }
);

BasicInfoSection.displayName = 'BasicInfoSection';
